import {
    type IndustryCodeDto,
    industryCodeDtoMap,
    type IndustryCodes,
} from "app/pages/sales-intelligence/domain/industry/types/codes";
import type { IIndustryCodesFilterDto } from "app/pages/sales-intelligence/domain/industry/types/filter-values";
import { FilterPanelBaseFilter } from "app/pages/sales-intelligence/modules/advanced-search-filtering/models/BaseFilter";
import type { IFilterPanelBaseFilterDeps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filters";
import type { FilterValueDelta } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/misc";
import { calculateArrayDelta } from "app/pages/sales-intelligence/modules/advanced-search-filtering/utils/calculateValueDeltaUtils";
import type { InclusionOption } from "app/pages/sales-intelligence/types/commonMaps";

import { IndustryCodesFilterValue } from "./IndustryCodesFilterValue";

type IndustryCodesFilterOptions = {
    readonly inclusionOptions: readonly InclusionOption[];
    readonly naicsCode: readonly IndustryCodes[];
    readonly sicCodes: readonly IndustryCodes[];
};

class IndustryCodesFilterModel extends FilterPanelBaseFilter<
    IIndustryCodesFilterDto,
    IndustryCodesFilterOptions,
    IIndustryCodesFilterDto | undefined
> {
    readonly types: readonly InclusionOption[];
    readonly codeOptions: readonly IndustryCodeDto[] = Object.values(industryCodeDtoMap);

    constructor(deps: IFilterPanelBaseFilterDeps<IIndustryCodesFilterDto, IndustryCodesFilterOptions>) {
        super(deps);

        this.types = deps.options.inclusionOptions;
    }

    fromApiValue(value?: IIndustryCodesFilterDto): IIndustryCodesFilterDto | undefined {
        return new IndustryCodesFilterValue(value).isEmpty() ? undefined : IndustryCodesFilterValue.toDtoValue(value);
    }

    toApiValue(value?: IIndustryCodesFilterDto): IIndustryCodesFilterDto | undefined {
        if (!value || new IndustryCodesFilterValue(value).isEmpty()) {
            return undefined;
        }

        return IndustryCodesFilterValue.toDtoValue(value);
    }

    getEmptyValue(): IIndustryCodesFilterDto | undefined {
        return undefined;
    }

    calculateValueDelta(previousValue?: IIndustryCodesFilterDto, newValue?: IIndustryCodesFilterDto): FilterValueDelta {
        const previousCodes = [...(previousValue?.naicsCodes ?? []), ...(previousValue?.sicCodes ?? [])];
        const newCodes = [...(newValue?.naicsCodes ?? []), ...(newValue?.sicCodes ?? [])];

        return calculateArrayDelta(previousCodes, newCodes);
    }

    isValueEmpty(value?: IIndustryCodesFilterDto): boolean {
        return new IndustryCodesFilterValue(value).isEmpty();
    }
}

export default IndustryCodesFilterModel;
